import { rebaseTileUrl } from '@civilgrid/utils/src/dev/tileProxy';
import { isFeatureFlagEnabled } from '~/common/posthog';
import getConfig from '~/config';
import { getAxiosClient } from './common';

const config = getConfig();
const axios = getAxiosClient();

/** PostHog flag key gating the collapsed tiles config: one data-driven layer per source instead of one per ESRI category. */
export const COLLAPSE_TILE_LAYERS_FLAG = 'tiles-collapse-unique-value-layers';

export async function getTilesConfig() {
  const { data } = await axios({
    method: 'GET',
    url: `${config.tileServerBase}/v1/tiles/config`,
    // Read imperatively because the caller is a store action, not a hook: a session that bootstraps
    // before PostHog resolves its flags asks for the per-value layer list until the next reload.
    params: isFeatureFlagEnabled(COLLAPSE_TILE_LAYERS_FLAG) ? { collapseLayers: '1' } : undefined,
  });

  return data;
}

export async function getCustomLayersTilesConfig() {
  const { data } = await axios.get(`${config.tileServerBase}/v1/tiles/customLayers/config`);
  return data;
}

export function addToken(url: string) {
  // Dev only: tile URLs arrive absolute from the tile server's own base, so point them at the vite
  // proxy (or a local tile server). Deployed environments use them exactly as returned.
  const _url = new URL(config.env === 'dev' ? rebaseTileUrl(url, config.tileServerBase) : url);
  const token = window.CG_TOKEN;
  // Cookie mode: no bearer token. Omit the param rather than append an empty `token=` (the tile
  // server treats it as a bad token); auth rides on the HttpOnly cookie, sent cross-origin by the
  // map's transformRequest (credentials: 'include'). TODO(INFRA-218): drop the param once rolled out.
  if (token) {
    _url.searchParams.append('token', token);
  }
  return decodeURI(_url.toString());
}

/**
 * Applies `addToken` to an `icon-image` / `fill-pattern` value, which the tile server emits either
 * as a bare image URL or — for a collapsed uniqueValue renderer — as a `match` expression whose
 * outputs are image URLs. Category labels are left alone: only output positions carry a URL.
 */
export function addTokenToImageValue(value: unknown): any {
  if (typeof value === 'string') return addToken(value);
  if (!Array.isArray(value) || value[0] !== 'match') return value;
  // ['match', input, label, output, …, default]: outputs are the odd indices from 3 on, and the last.
  return value.map((element, i) =>
    i >= 3 && (i % 2 === 1 || i === value.length - 1) ? addTokenToImageValue(element) : element,
  );
}
